import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderStatusEnum } from '@koob/enums';
import { KoobKanbanColumn } from '@koob/ui';
import folderStatusColor from './folderStatusColor';
import { setFolderDragData } from './folderDragData';

// Column order + label keys (Ongoing maps to CONFIRMING), matching the filters.
const STATUS_ORDER = [
  FolderStatusEnum.NEW,
  FolderStatusEnum.DRAFT,
  FolderStatusEnum.CONFIRMING,
  FolderStatusEnum.CONFIRMED,
  FolderStatusEnum.ON_SPOT,
  FolderStatusEnum.COMPLETED,
  FolderStatusEnum.CANCELLED,
  FolderStatusEnum.ARCHIVED,
];

const STATUS_LABEL_KEY = {
  [FolderStatusEnum.NEW]: 'new',
  [FolderStatusEnum.DRAFT]: 'draft',
  [FolderStatusEnum.CONFIRMING]: 'ongoing',
  [FolderStatusEnum.CONFIRMED]: 'confirmed',
  [FolderStatusEnum.ON_SPOT]: 'onSpot',
  [FolderStatusEnum.COMPLETED]: 'completed',
  [FolderStatusEnum.CANCELLED]: 'cancelled',
  [FolderStatusEnum.ARCHIVED]: 'archived',
};

function getGuest(folder) {
  if (!folder?.travelers?.length) return null;
  const first = folder.travelers[0];
  return {
    name: `${first.firstName ?? ''} ${first.lastName ?? ''}`.trim(),
    count: folder.travelers.length,
  };
}

// Agent initials-style short name: "Paul M."
function getAgent(folder) {
  const user = folder?.createdBy;
  if (!user) return null;
  const last = user.lastName ? ` ${user.lastName[0]}.` : '';
  return `${user.firstName ?? ''}${last}`.trim() || null;
}

function getProvider(folder) {
  const booking = folder?.bookings?.[0];
  if (!booking) return null;
  if (booking.kind === 'dmc') {
    return (
      booking.hotel?.organization?.displayName ??
      booking.experiencesBooked?.[0]?.experience?.organizationName ??
      null
    );
  }
  return 'BedBank';
}

function getComposition(folder) {
  const bookings = folder?.bookings ?? [];
  return {
    hotels: bookings.filter((b) => b.hotel).length,
    experiences: bookings.reduce(
      (total, b) => total + (b.experiencesBooked?.length ?? 0),
      0,
    ),
  };
}

// A single folder card. Clicking activates the folder + navigates (same action
// as the table row in US-011) — wired by the caller via onClick. Also
// draggable so it can be dropped onto the sidebar ActiveFolderCard.
function KanbanCard({ folder, onClick }) {
  const { t } = useTranslation('folders');
  const guest = getGuest(folder);
  const agent = getAgent(folder);
  const provider = getProvider(folder);
  const { hotels, experiences } = getComposition(folder);

  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => setFolderDragData(e.dataTransfer, folder)}
      onClick={() => onClick?.(folder)}
      className="flex w-full flex-col gap-2 rounded-lg border border-solid border-border bg-background p-3 text-left transition-shadow hover:shadow-sm cursor-pointer"
    >
      {folder?.icr && (
        <span className="truncate text-xs font-medium text-brand">
          {folder.icr}
        </span>
      )}

      <p className="m-0 truncate text-sm font-semibold leading-tight text-foreground">
        {folder?.name}
      </p>

      {guest && (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-xs font-medium text-foreground">
            {guest.name}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <i className="fa-light fa-users" aria-hidden="true" />
            {t('kanban.nGuests', { count: guest.count })}
          </span>
        </span>
      )}

      <div className="flex items-center gap-3 border-0 border-t border-solid border-border pt-1 text-xs text-muted-foreground">
        <span
          className="inline-flex items-center gap-1"
          title={t('kanban.hotels')}
        >
          <i className="fa-light fa-hotel" aria-hidden="true" />
          {hotels}
        </span>
        <span className="text-border">/</span>
        <span
          className="inline-flex items-center gap-1"
          title={t('kanban.experiences')}
        >
          <i className="fa-light fa-sparkles" aria-hidden="true" />
          {experiences}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="truncate">{agent ?? '—'}</span>
        <span className="inline-flex min-w-0 items-center gap-1">
          <i className="fa-light fa-building" aria-hidden="true" />
          <span className="truncate">{provider ?? '—'}</span>
        </span>
      </div>
    </button>
  );
}

/**
 * Kanban board of client folders grouped by status.
 *
 * Presentational — `getStatus(folder)` (folder status is derived client-side)
 * is passed in by the caller. One column per visible status (hidden statuses
 * are omitted, shared with the status-filter row). Clicking a card calls
 * `onCardClick(folder)` — the caller activates the folder + navigates.
 */
export default function FolderKanban({
  folders = [],
  getStatus,
  hiddenStatuses = [],
  onCardClick,
}) {
  const { t } = useTranslation('folders');
  const visibleStatuses = STATUS_ORDER.filter(
    (status) => !hiddenStatuses.includes(status),
  );

  const grouped = {};
  visibleStatuses.forEach((status) => {
    grouped[status] = [];
  });
  folders.forEach((folder) => {
    const status = getStatus ? getStatus(folder) : FolderStatusEnum.NEW;
    if (grouped[status]) {
      grouped[status].push(folder);
    }
  });

  return (
    <div className="flex items-start gap-4 overflow-x-auto pb-4">
      {visibleStatuses.map((status) => (
        <KoobKanbanColumn
          key={status}
          color={folderStatusColor(status)}
          title={t(`statusFilters.${STATUS_LABEL_KEY[status]}`)}
          count={grouped[status].length}
        >
          {grouped[status].length > 0 ? (
            grouped[status].map((folder) => (
              <KanbanCard
                key={folder.id}
                folder={folder}
                onClick={onCardClick}
              />
            ))
          ) : (
            <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
              {t('kanban.empty')}
            </div>
          )}
        </KoobKanbanColumn>
      ))}
    </div>
  );
}
